"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { cn } from "@perceptron/utils/cn";
import type { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";

import { MODES, type Mode } from "@demo/constants/mode";

import ModeCard from "./ModeCard";

type ModeCarouselVariant = "desktop" | "mobile";

type ModeCarouselProps = {
  /** The app's currently-active mode (drives the "Active" card state). */
  currentMode: Mode;
  /** Switch the app to a mode (also closes the selector via the parent). */
  onSwitch: (mode: Mode) => void;
  /** Slide to open on (jumped to without animation on mount). */
  initialIndex: number;
  /** Layout: wide dialog with two cards side by side, or full-width sheet. */
  variant: ModeCarouselVariant;
};

/** Centered row of dots that scrolls embla on click. Mirrors FeaturedCarousel. */
const PaginationDots = ({
  count,
  activeIndex,
  goTo,
  className,
}: {
  count: number;
  activeIndex: number;
  goTo: (i: number) => void;
  className?: string;
}) => (
  <div aria-hidden="true" className={cn("flex items-center justify-center gap-1.5", className)}>
    {Array.from({ length: count }).map((_, i) => (
      <button
        aria-current={i === activeIndex ? "true" : undefined}
        aria-label={`Go to slide ${i + 1}`}
        className="-m-2 flex cursor-pointer items-center justify-center p-2"
        key={i}
        tabIndex={-1}
        type="button"
        onClick={() => goTo(i)}
      >
        <span
          aria-hidden="true"
          className={cn(
            "block rounded-full transition-all",
            i === activeIndex
              ? "h-1.5 w-2.5 bg-foreground"
              : "size-1.5 bg-foreground-disabled hover:bg-foreground-secondary",
          )}
        />
      </button>
    ))}
  </div>
);

/**
 * Horizontal embla carousel of mode cards. Desktop shows two cards side by
 * side (half-width slides, no inactive styling); mobile opens centered on the
 * current mode and shows one full-width card per slide.
 */
const ModeCarousel = ({ currentMode, onSwitch, initialIndex, variant }: ModeCarouselProps) => {
  const isDesktop = variant === "desktop";

  const wheelPlugins = useMemo(() => (isDesktop ? [WheelGesturesPlugin({ forceWheelAxis: "y" })] : []), [isDesktop]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      axis: "x",
      align: isDesktop ? "start" : "center",
      loop: false,
      containScroll: isDesktop ? "trimSnaps" : false,
      skipSnaps: false,
    },
    wheelPlugins,
  );

  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [snapCount, setSnapCount] = useState<number>(MODES.length);

  const goTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  // Jump (no animation) to the current mode when the carousel initializes.
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.scrollTo(initialIndex, true);
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, initialIndex]);

  // Track the selected snap and how many snaps exist (desktop shows two slides
  // per snap, so snaps !== slides there). Mirrors FeaturedCarousel's wiring.
  useEffect(() => {
    if (!emblaApi) return;
    const onSelectChange = (api: EmblaCarouselType) => {
      setSelectedIndex(api.selectedScrollSnap());
      setSnapCount(api.scrollSnapList().length);
    };

    onSelectChange(emblaApi);
    emblaApi.on("select", onSelectChange).on("reInit", onSelectChange);

    return () => {
      emblaApi.off("select", onSelectChange).off("reInit", onSelectChange);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") emblaApi.scrollPrev();
      else if (event.key === "ArrowRight") emblaApi.scrollNext();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [emblaApi]);

  return (
    <div className="w-full">
      <div className="overflow-hidden px-0 sm:px-20" ref={emblaRef}>
        <div className="flex py-2">
          {MODES.map((mode, i) => {
            // Desktop shows both cards at once, so every slide stays active.
            const isActive = isDesktop || i === selectedIndex;
            return (
              <div
                className={cn("transition-all duration-200", {
                  "relative min-w-0 shrink-0 grow-0 basis-1/2 px-4": isDesktop,
                  "min-w-0 shrink-0 grow-0 px-2": !isDesktop,
                  "scale-100 opacity-100 blur-0": isActive,
                  "cursor-pointer opacity-80 blur-[6px]": !isActive,
                })}
                key={mode}
              >
                <ModeCard
                  isActive={isActive}
                  isCurrent={MODES[i] === currentMode}
                  mode={mode}
                  onSwitch={() => onSwitch(MODES[i]!)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {snapCount > 1 && (
        <PaginationDots
          activeIndex={selectedIndex}
          className={isDesktop ? "mt-6" : "mt-4"}
          count={snapCount}
          goTo={goTo}
        />
      )}
    </div>
  );
};

export default ModeCarousel;
